/**
 * The Needs attention column (WBS-12652/12653): why a Visit will not get its Job, or its Job will not finish,
 * unless a person acts, with the detail behind it on hover. The server decides the reason (`visitNeedsAttention`); this only
 * names it and carries the overlay it came from — the stamper's message, the approval's review reason, or
 * what the line's Jobs lack and where to set it.
 */
import { VisitBrowseRow, visitJobReadinessSentence, VisitNeedsAttentionReason } from '@jms/model';
import { defineConditionalTag } from '../../shared/define.conditional.tag';
import type { StatusTone } from '../../shared/status.tag.tones';

// NotCreated is only ever derived once the appointment has passed, and a passed appointment cannot be built
// (`visitConstructionGate`), so it is a miss, not work still pending: "Not created" read as if a Job were coming.
const LABELS: Record<VisitNeedsAttentionReason, string> = {
  [VisitNeedsAttentionReason.NotCreated]: 'Missed',
  [VisitNeedsAttentionReason.JobNotFinished]: 'Job not finished',
  [VisitNeedsAttentionReason.JobCreationFailed]: 'Job creation failed',
  [VisitNeedsAttentionReason.ChangedSinceApproval]: 'Approve again',
  [VisitNeedsAttentionReason.CannotCreateJob]: 'Can\'t create job',
};

// A missed or failed build and a late Job are already late; the other two can still be fixed before the Job is due.
const TONES: Record<VisitNeedsAttentionReason, StatusTone> = {
  [VisitNeedsAttentionReason.NotCreated]: 'error',
  [VisitNeedsAttentionReason.JobNotFinished]: 'error',
  [VisitNeedsAttentionReason.JobCreationFailed]: 'error',
  [VisitNeedsAttentionReason.ChangedSinceApproval]: 'warning',
  [VisitNeedsAttentionReason.CannotCreateJob]: 'warning',
};

export const visitNeedsAttentionTag = defineConditionalTag<VisitBrowseRow>(row => {

  const reason = row?.needsAttention;
  // The Job is what a build reason was waiting on; a reason from an earlier read must not outlive it. Only a late Job's reason belongs on a Job'd row.
  if (!reason || !(reason in LABELS)) return null;
  if (row.job?._id && reason !== VisitNeedsAttentionReason.JobNotFinished) return null;
  const detail = reasonDetail(row, reason);
  return { label: LABELS[reason], tone: TONES[reason], ...(detail ? { tooltip: detail } : {}) };
});

function reasonDetail(row: VisitBrowseRow, reason: VisitNeedsAttentionReason): string | undefined {

  if (reason === VisitNeedsAttentionReason.CannotCreateJob) return row.jobReadinessDetail?.length ? visitJobReadinessSentence(row.jobReadinessDetail, 'Jobs need') : undefined;
  if (reason === VisitNeedsAttentionReason.ChangedSinceApproval) return 'Tasks changed since approval. Review and approve them again.';
  return row.jobCreationIssue?.reason?.trim() || undefined;
}
